import React, { useRef } from 'react';
import {
  Select as AriaSelect,
  type SelectProps as AriaSelectProps,
  Button,
  composeRenderProps,
  ListBox,
  type ListBoxItemProps,
  SelectValue,
  type ValidationResult,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { Description, FieldError, Label } from './field';
import { DropdownItem, DropdownSection, type DropdownSectionProps } from './list-box';
import { Popover } from './popover';
import { composeTailwindRenderProps, focusRing } from './component-utils';
import { InputChevron } from './input-chevron';
import { ScrollArea } from './scroll-area';
import type { Prettify } from 'types/utils';
import type { ButtonProps } from './button';

// TODO: reemplazar input por box
const styles = tv({
  extend: focusRing,
  base: 'relative flex w-full cursor-default items-center justify-between gap-4 border text-start text-sm transition enabled:group-invalid:border-error disabled:text-disabled lg:text-xs disabled:forced-colors:border-[GrayText] disabled:forced-colors:text-[GrayText]',
  variants: {
    variant: {
      default:
        'h-8 min-w-[150px] border-dark px-1.5 disabled:border-disabled lab:has-[span[data-placeholder=true]]:border-subtle lab:disabled:border-subtle lg:h-6',
      dark: 'h-8 min-w-[150px] border-dark px-1.5 disabled:border-disabled lab:has-[span[data-placeholder=true]]:border-dark lab:disabled:border-subtle lg:h-6',
      empty:
        'lab:has-[span[data-placeholder=true]]:text-bold gap-1 border-transparent px-0 enabled:group-invalid:text-error disabled:bg-disabled/10 disabled:forced-colors:text-[GrayText]',
      box: 'min-h-[52px] gap-1 px-3 aria-[expanded=true]:border-dark',
    },
  },
});

const transparentStyles = tv({
  base: 'flex h-8 w-full cursor-default items-center gap-4 border px-1.5 text-start text-sm outline-none transition lab:lg:h-6 lab:lg:text-xs',
  variants: {
    color: {
      black: 'border-transparent bg-neutral-inverted/5 group-focus:border-b-dark pressed:border-b-dark',
      white:
        'border-transparent bg-neutral/20 text-inverted placeholder:text-inverted invalid:border-b-error focus:border-b-clear invalid:forced-colors:border-[Mark]',
    },
    isDisabled: {
      true: '!border-transparent !bg-disabled !text-disabled forced-colors:border-[GrayText]',
    },
    isOpen: {},
    isInvalid: {},
    isRequired: {},
  },
});

type TSelectItem = {
  label: string;
  value: string;
};

type DefaultSelectProps<T extends TSelectItem> = {
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  items: Iterable<T>;
  triggerClassName?: ButtonProps['className'];
  children?: (item: T) => React.ReactNode;
};

export type SelectProps<T extends TSelectItem> = Prettify<
  | ({
      variant?: 'default' | 'transparent' | 'dark' | 'empty';
      label?: string;
      hiddenLabel?: boolean;
      color?: 'white' | 'black';
      placeholder?: string;
    } & DefaultSelectProps<T> &
      Omit<AriaSelectProps<T>, 'children'>)
  | ({
      variant: 'box';
      label: string;
      hiddenLabel?: never;
      color?: never;
    } & DefaultSelectProps<T> &
      Omit<AriaSelectProps<T>, 'children'>)
>;

export function Select<T extends TSelectItem>({ variant = 'default', ...props }: SelectProps<T>) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  if (variant === 'box') {
    const { label, triggerClassName, className, description, errorMessage, items, children, ...rest } = props;
    return (
      <AriaSelect
        {...rest}
        placeholder={label ?? ''}
        className={composeTailwindRenderProps(className, 'group relative flex min-w-[250px] flex-col gap-1 disabled:bg-dim')}
      >
        <Label className="sr-only">{label}</Label>
        <Button
          ref={triggerRef}
          data-variant={variant}
          className={composeRenderProps(triggerClassName, (className, renderProps: any) => styles({ ...renderProps, variant: 'box', className }))}
        >
          <SelectValue className={({ isPlaceholder }) => `flex-grow truncate text-ellipsis text-sm ${isPlaceholder ? 'text-transparent' : ''}`}>
            {({ defaultChildren, isPlaceholder }) => {
              return (
                <>
                  <span className="inline-flex max-w-full pt-1 [&>span]:max-w-full [&_svg]:hidden">{defaultChildren}</span>
                  <span
                    aria-hidden
                    className={`absolute left-3 text-muted transition-all group-disabled:text-disabled ${!isPlaceholder ? 'top-0.5 text-xxs' : ''}`}
                  >
                    {label}
                  </span>
                </>
              );
            }}
          </SelectValue>
          <InputChevron aria-hidden className="group-open:rotate-180 group-disabled:text-disabled" />
        </Button>
        {description && <Description>{description}</Description>}
        <FieldError>{errorMessage}</FieldError>
        <Popover className="min-w-[--trigger-width]" offset={2}>
          <ScrollArea className="overflow-hidden">
            <ListBox items={items} className="max-h-52 min-h-0">
              {children ??
                ((item) => (
                  <DropdownItem id={item.value} key={item.value} value={item}>
                    {item.label}
                  </DropdownItem>
                ))}
            </ListBox>
          </ScrollArea>
        </Popover>
      </AriaSelect>
    );
  } else {
    const { label, triggerClassName, className, description, hiddenLabel, color, errorMessage, items, children, ...rest } = props;
    return (
      <AriaSelect {...rest} className={composeTailwindRenderProps(className, 'group flex flex-col gap-1')}>
        {label && <Label className={hiddenLabel ? 'sr-only' : ''}>{label}</Label>}
        <Button
          ref={triggerRef}
          data-variant={variant}
          className={composeRenderProps(triggerClassName, (className, renderProps: any) => {
            if (variant === 'transparent') {
              return transparentStyles({ ...renderProps, className, color: color ?? 'black' });
            }
            return styles({ ...renderProps, variant, className });
          })}
        >
          <SelectValue
            className={`text-sm lg:text-xs [&_svg]:hidden ${variant !== 'dark' ? 'data-[placeholder=true]:text-bold' : 'flex-grow uppercase data-[placeholder=true]:text-copy'} `}
          >
            {({ defaultChildren, isPlaceholder }) => {
              return isPlaceholder ? (props?.placeholder ?? defaultChildren) : defaultChildren;
            }}
          </SelectValue>
          <InputChevron aria-hidden className="group-open:rotate-180 group-disabled:text-disabled" />
        </Button>
        {description && <Description>{description}</Description>}
        <FieldError>{errorMessage}</FieldError>
        <Popover triggerRef={triggerRef} className="min-w-[--trigger-width]" border>
          <ScrollArea className="overflow-hidden">
            <ListBox items={items} className="max-h-52 min-h-0">
              {children ??
                ((item) => (
                  <DropdownItem id={item.value} key={item.value} value={item}>
                    {item.label}
                  </DropdownItem>
                ))}
            </ListBox>
          </ScrollArea>
        </Popover>
      </AriaSelect>
    );
  }
}

export function SelectItem(props: ListBoxItemProps) {
  return <DropdownItem {...props} />;
}

export function SelectSection<T extends object>(props: DropdownSectionProps<T>) {
  return <DropdownSection {...props} />;
}
